/**
 * Pulse milestone-based goal progress logic.
 *
 * Users progress through milestones at 1, 5, 10, 25, 50, 100, 150, 250, 500 Pulses.
 * The progress bar shows advancement within the current tier toward the next milestone.
 */

export const PULSE_MILESTONES = [1, 5, 10, 25, 50, 100, 150, 250, 500] as const;

export interface PulseGoalProgress {
  /** The milestone the user is currently working toward */
  currentGoal: number;
  /** The previous milestone (tier baseline), or 0 if working toward first milestone */
  previousMilestone: number;
  /** How many Pulses completed within this tier */
  progressInTier: number;
  /** Total Pulses needed to complete this tier */
  tierSize: number;
  /** Percentage fill for the progress bar (0-100) */
  percentage: number;
  /** True if user has reached the maximum milestone (500) */
  isMaxedOut: boolean;
}

/**
 * Derives the goal progress state for the Pulse milestone system.
 *
 * @param pulsesCompleted - Total number of Pulses the user has completed
 * @returns Progress information for rendering the goal bar
 *
 * @example
 * derivePulseGoalProgress(0)  // → { currentGoal: 1, progressInTier: 0, tierSize: 1, percentage: 0 }
 * derivePulseGoalProgress(1)  // → { currentGoal: 5, progressInTier: 0, tierSize: 4, percentage: 20 }
 * derivePulseGoalProgress(7)  // → { currentGoal: 10, progressInTier: 2, tierSize: 5, percentage: 70 }
 * derivePulseGoalProgress(500) // → { currentGoal: 500, progressInTier: 250, tierSize: 250, percentage: 100, isMaxedOut: true }
 */
export function derivePulseGoalProgress(
  pulsesCompleted: number
): PulseGoalProgress {
  const maxMilestone = PULSE_MILESTONES[PULSE_MILESTONES.length - 1];

  // Edge case: user has reached or exceeded max milestone
  if (pulsesCompleted >= maxMilestone) {
    const previousMilestone =
      PULSE_MILESTONES[PULSE_MILESTONES.length - 2] ?? 0;
    return {
      currentGoal: maxMilestone,
      previousMilestone,
      progressInTier: maxMilestone - previousMilestone,
      tierSize: maxMilestone - previousMilestone,
      percentage: 100,
      isMaxedOut: true
    };
  }

  // Find the next milestone (first one greater than pulsesCompleted)
  const currentGoalIndex = PULSE_MILESTONES.findIndex(m => m > pulsesCompleted);
  const currentGoal = PULSE_MILESTONES[currentGoalIndex];

  // Previous milestone is the one before currentGoal, or 0 if at first tier
  const previousMilestone =
    currentGoalIndex > 0 ? PULSE_MILESTONES[currentGoalIndex - 1] : 0;

  const tierSize = currentGoal - previousMilestone;
  const progressInTier = pulsesCompleted - previousMilestone;
  // Use absolute progress toward goal (pulsesCompleted/currentGoal) for visual fill
  // This ensures 1/5 shows 20%, not 0% (tier-relative)
  const percentage =
    currentGoal > 0 ? Math.round((pulsesCompleted / currentGoal) * 100) : 0;

  return {
    currentGoal,
    previousMilestone,
    progressInTier,
    tierSize,
    percentage,
    isMaxedOut: false
  };
}
